'use client';

import { DOMAIN } from '@/lib/config';
import { useAppSelector } from '@/lib/redux/hooks';
import { buildDocumentPath } from '@/lib/utils/documentMetadata';
import { type ExternalUrlInfo, buildPreviewHref } from '@/utils/previewUtils';
import {
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  FileText,
  Globe,
  Quote,
  X,
} from 'lucide-react';
import { useLocale } from 'next-intl';
import React, { useMemo } from 'react';

/**
 * ExternalLinkPreview Component
 *
 * Displays a two-row preview card for external URLs without fetching content.
 * Row 1: Citation number, favicon, and breadcrumb path (domain → page → subpage)
 * Row 2: Full clickable URL with copy functionality
 *
 * This is a lightweight alternative to full Open Graph previews,
 * displaying only information extractable from the URL itself.
 *
 * @see Issue #880 for full Open Graph metadata fetching implementation
 */

interface ExternalLinkPreviewProps {
  urlInfo: ExternalUrlInfo;
  width?: number | undefined;
  citationNumber?: number | undefined;
  isHighlighted?: boolean | undefined;
}

/**
 * Parse a URL path into breadcrumb segments.
 * Converts "/path/to/page" into ["path", "to", "page"]
 * Handles URL-encoded characters and filters empty segments.
 */
function parsePathToBreadcrumbs(path: string): string[] {
  if (!path || path === '/') return [];

  return path
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      // Decode URL-encoded characters
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    });
  // Keep original formatting - preserves technical identifiers like "api-v2-endpoint"
}

// Cap the visible excerpt length to keep the hover-card readable. Past ~280
// chars Gemini snippets become two-paragraph quotes that bloat the popover
// without adding signal.
const EXCERPT_MAX_CHARS = 280;
// Cap how many excerpts we render per card. Beyond two the user is generally
// better served by clicking through to the actual page.
const EXCERPT_MAX_COUNT = 2;

function truncateExcerpt(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= EXCERPT_MAX_CHARS) return trimmed;
  return `${trimmed.slice(0, EXCERPT_MAX_CHARS - 1).trimEnd()}…`;
}

export function ExternalLinkPreview({
  urlInfo,
  width = 400,
  citationNumber,
  isHighlighted = false,
}: ExternalLinkPreviewProps) {
  const [copied, setCopied] = React.useState(false);
  const [copyFailed, setCopyFailed] = React.useState(false);
  const [faviconError, setFaviconError] = React.useState(false);

  // #3817: pull rich source info (page title + grounded excerpt) from the
  // web_sources_map SSE event when available. Falls back to URL-derived
  // breadcrumbs when the URL wasn't surfaced via Vertex grounding.
  const webSource = useAppSelector(
    (state) => state.researchSession.webSourcesMap[urlInfo.url]
  );
  const groundedTitle = webSource?.title?.trim() || null;
  const excerpts = useMemo(() => {
    if (!webSource?.excerpts) return [] as string[];
    return webSource.excerpts.slice(0, EXCERPT_MAX_COUNT).map(truncateExcerpt);
  }, [webSource?.excerpts]);

  // #5987 / #5949: when the backend resolved this authoritative web hit to an
  // internal Lexomat doc, present + link it as the internal section-anchored
  // document instead of the raw slov-lex / EUR-Lex URL. Falls back to the
  // external URL when unresolved (current behavior unchanged).
  const locale = useLocale();
  const internalDocId =
    webSource?.resolved_to_internal === true &&
    webSource?.internal_doc_id != null
      ? webSource.internal_doc_id
      : null;
  const isInternalResolved = internalDocId !== null;
  // Absolute href to the section-anchored internal Lexomat document
  // (buildDocumentPath is the single source of truth for /<locale>/<slug>/<id>;
  // buildPreviewHref prepends the host so the new tab lands on the app).
  const effectiveHref = useMemo(
    () =>
      isInternalResolved
        ? buildPreviewHref(
            buildDocumentPath(locale, String(internalDocId)),
            DOMAIN
          )
        : urlInfo.url,
    [isInternalResolved, internalDocId, locale, urlInfo.url]
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(effectiveHref, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(effectiveHref);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2000);
    }
  };

  // Parse path into breadcrumb segments
  const breadcrumbs = useMemo(
    () => parsePathToBreadcrumbs(urlInfo.path),
    [urlInfo.path]
  );

  return (
    <div
      className='w-full bg-surface-card shadow-sm'
      style={{
        maxWidth: `${width}px`,
      }}
      aria-label={`External link to ${urlInfo.displayTitle}`}
    >
      {/* Row 1: Citation number, favicon, breadcrumb path */}
      <div className='bg-surface-subtle px-3 py-2 flex items-center gap-2'>
        {citationNumber && (
          <div
            className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors ${
              isHighlighted
                ? 'bg-brand-tertiary-hover text-content-inverse'
                : 'bg-brand-tertiary text-content-inverse'
            }`}
          >
            {citationNumber}
          </div>
        )}
        {/* #5987: internal-resolved sources show the Lexomat doc icon; external
            sources keep the favicon with Globe fallback on error. */}
        {isInternalResolved ? (
          <FileText
            className='w-4 h-4 text-brand-tertiary flex-shrink-0'
            aria-hidden='true'
          />
        ) : faviconError ? (
          <Globe
            className='w-4 h-4 text-content-muted flex-shrink-0'
            aria-hidden='true'
          />
        ) : (
          <img
            src={urlInfo.faviconUrl}
            alt=''
            className='w-4 h-4 flex-shrink-0'
            onLoad={() => setFaviconError(false)}
            onError={() => setFaviconError(true)}
          />
        )}

        {/* Header content: grounded title (when available) wins over breadcrumbs.
            #5987: internal-resolved sources show the Lexomat brand on the
            secondary/leading line instead of the external domain. */}
        {groundedTitle ? (
          <div className='flex flex-col flex-1 min-w-0 overflow-hidden'>
            <span
              className='text-xs font-semibold text-content-primary truncate'
              title={groundedTitle}
            >
              {groundedTitle}
            </span>
            <span className='text-[11px] text-content-secondary truncate'>
              {isInternalResolved ? 'Lexomat' : urlInfo.domain}
            </span>
          </div>
        ) : (
          /* Breadcrumb path: domain → page → subpage */
          <div className='flex items-center gap-1 flex-1 min-w-0 overflow-hidden'>
            {/* Domain (always shown) */}
            <span className='text-xs font-medium text-content-secondary flex-shrink-0'>
              {isInternalResolved ? 'Lexomat' : urlInfo.domain}
            </span>

            {/* Path segments as breadcrumbs */}
            {breadcrumbs.map((segment, index) => (
              <React.Fragment key={index}>
                <ChevronRight className='w-3 h-3 text-content-muted flex-shrink-0' />
                <span
                  className={`text-xs text-content-secondary truncate ${
                    index === breadcrumbs.length - 1
                      ? 'font-medium'
                      : 'max-w-[100px]'
                  }`}
                  title={segment}
                >
                  {segment}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* External link icon (clickable to open) */}
        <button
          onClick={handleClick}
          className='min-w-[44px] min-h-[44px] p-2.5 hover:bg-surface-muted rounded transition-colors flex-shrink-0 flex items-center justify-center'
          aria-label={
            isInternalResolved ? 'Open in Lexomat' : 'Open in new tab'
          }
          title={isInternalResolved ? 'Open in Lexomat' : 'Open in new tab'}
        >
          <ExternalLink className='w-4 h-4 text-content-muted' />
        </button>
      </div>

      {/* Row 1.5 (#3817): grounded excerpts — only when Gemini surfaced them */}
      {excerpts.length > 0 && (
        <ul className='border-t border-line-subtle px-3 py-2 flex flex-col gap-1.5 list-none m-0'>
          {excerpts.map((text, idx) => (
            <li
              key={idx}
              className='flex items-start gap-1.5 text-[11px] leading-snug text-content-secondary'
            >
              <Quote
                className='w-3 h-3 text-content-muted flex-shrink-0 mt-0.5'
                aria-hidden='true'
              />
              <span className='flex-1 italic'>{text}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Row 2: Full URL with copy button */}
      <div className='px-3 py-1.5 flex items-center gap-2 border-t border-line-subtle'>
        <a
          href={effectiveHref}
          target='_blank'
          rel='noopener noreferrer'
          onClick={(e) => e.stopPropagation()}
          className='text-xs text-status-info hover:text-status-info-hover hover:underline truncate flex-1 min-w-0'
          title={effectiveHref}
        >
          {effectiveHref}
        </a>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className='min-w-[44px] min-h-[44px] p-2.5 hover:bg-surface-muted rounded transition-colors flex-shrink-0 flex items-center justify-center'
          aria-label={
            copyFailed ? 'Copy failed' : copied ? 'Copied!' : 'Copy URL'
          }
          title={copyFailed ? 'Copy failed' : copied ? 'Copied!' : 'Copy URL'}
        >
          {copyFailed ? (
            <X className='w-3.5 h-3.5 text-status-error' />
          ) : copied ? (
            <Check className='w-3.5 h-3.5 text-status-success' />
          ) : (
            <Copy className='w-3.5 h-3.5 text-content-muted hover:text-content-secondary' />
          )}
        </button>
      </div>
    </div>
  );
}
